// UploadFiles Store 统一导出，Direct Merge 合并 taskStore 和 modalStore

import { getCurrentAccountId } from "@helium10/re-core";
import type { ILoadFile } from "@helium10/re-ui-components";
import { create } from "zustand";

import { getUploadHistoryDetail, type IDetailResponse } from "@/components/UploadHistory/request";
import { type IInboundFields, type IUploadSingleFileResponse } from "@/features/inbound/requests";

import { callbackResult } from "../request";

// ========== 类型导出 ==========
export type {
  IUploadDrawerState,
  IUploadModalState,
  IUploadSimpleModalState,
  UploadModalMode,
} from "./modalStore";
export type {
  InboundConfirmDetailCallbackPayload,
  IUploadFileItem,
  IUploadFileItemStatus,
  IUploadTask,
  UploadTaskStatus,
} from "./taskStore";

// ========== 工具函数导出 ==========
export { calculateFileMD5 } from "./taskStore";

// ========== 校验常量（与 en.json fileUploadZone 一致）==========
export const MAX_SINGLE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_TOTAL_SIZE = 1024 * 1024 * 1024; // 1GB
export const MAX_FILES_COUNT = 200;

// 每个 Shipment ID 下，用户上传（type !== "system"）文件的最大数量
export const MAX_CUSTOMER_FILES_PER_SHIPMENT = 4;

// 统计已存在文件中用户上传（customer）的数量，type === "system" 不计入
export const getCustomerFilesCount = (files: { type?: string }[] | null | undefined): number =>
  files?.filter((f) => f.type !== "system").length ?? 0;

// 获取文件唯一标识，优先 webkitRelativePath/relativePath，否则 metadata.name
export const getFileKey = (file: ILoadFile): string => {
  const meta = file.metadata as File & { webkitRelativePath?: string; relativePath?: string };
  return meta?.webkitRelativePath || meta?.relativePath || file.metadata.name;
};

// ========== 导入内部类型 ==========
import type {
  IUploadDrawerState,
  IUploadModalState,
  IUploadSimpleModalState,
  UploadModalMode,
} from "./modalStore";
import type {
  InboundConfirmDetailCallbackPayload,
  IUploadFileItem,
  IUploadFileItemStatus,
  IUploadTask,
  UploadTaskStatus,
} from "./taskStore";

// ========== 合并后的 Store 接口 ==========
export interface IUploadFilesStore {
  tasks: Record<string, IUploadTask>;

  // ========== 任务管理方法 ==========
  getOrCreateTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => IUploadTask;
  addFiles: (taskId: string, files: ILoadFile[]) => void;
  removeFiles: (taskId: string, fileNames: string[]) => void;
  updateFileStatus: (
    taskId: string,
    fileName: string,
    status: IUploadFileItemStatus,
    result?: IUploadFileItem["result"],
  ) => void;
  clearTask: (taskId: string) => void;
  clearBulkUploadTasks: () => void;
  resetTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => void;
  updateTaskStatus: (taskId: string, status: UploadTaskStatus) => void;
  getTask: (taskId: string) => IUploadTask | undefined;
  hasUploadingTasks: () => boolean;
  // S3+callback；非 Confirm 维持原 onComplete；Confirm 且 inboundConfirmAfterS3FetchDetail 时走 onInboundConfirmDetail，不再用本地 error 决定 confirm/Result
  uploadFilesToS3: (
    taskId: string,
    uploadResponse: IUploadSingleFileResponse,
    onComplete?: (taskStatus: "uploaded" | "error", meta?: { batchId: string }) => void,
    options?: {
      exposeBatchIdForResultModal?: boolean;
      inboundConfirmAfterS3FetchDetail?: boolean;
      onInboundConfirmDetail?: (
        result: InboundConfirmDetailCallbackPayload,
      ) => void | Promise<void>;
    },
  ) => Promise<void>;

  // ========== 弹窗/抽屉状态（三个独立的状态空间）==========
  uploadModal: IUploadModalState;
  uploadDrawer: IUploadDrawerState;
  uploadSimpleModal: IUploadSimpleModalState;

  // ========== UploadModal 控制器方法 ==========
  openUploadModal: (data: IInboundFields) => void;
  closeUploadModal: () => void;
  setUploadModalMode: (mode: UploadModalMode) => void;
  setUploadModalConfirming: (confirming: boolean) => void;
  // 写入 uploadModal.resultBatchId，供 UploadResultFailedTabContent 请求 document-center detail
  setUploadModalResultBatchId: (batchId: string | null) => void;
  // 写入 uploadModal.resultDetailSnapshot，Confirm+detail 有失败行时失败 Tab 免二次请求
  setUploadModalResultDetailSnapshot: (data: IDetailResponse["data"] | null) => void;
  resetUploadModal: () => void;

  // ========== UploadDrawer 控制器方法 ==========
  openUploadDrawer: (data: any) => void;
  closeUploadDrawer: () => void;
  setUploadDrawerMode: (mode: UploadModalMode) => void;
  setUploadDrawerConfirming: (confirming: boolean) => void;
  resetUploadDrawer: () => void;

  // ========== UploadSimpleModal 控制器方法 ==========
  openUploadSimpleModal: (data: any) => void;
  closeUploadSimpleModal: () => void;
  setUploadSimpleModalConfirming: (confirming: boolean) => void;
  resetUploadSimpleModal: () => void;
}

// S3 上传工具：使用 PUT 方法上传文件到预签名 URL
const uploadToS3 = async (uploadUrl: string, fileBlob: Blob): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: fileBlob,
    headers: {
      "Content-Type": fileBlob.type || "application/octet-stream",
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
};

/**
 * 并发控制上传队列
 * 限制同时上传的文件数量为5个，避免对后端和S3造成压力
 */
class UploadQueue {
  private queue: Array<() => Promise<void>> = [];
  private running = 0;
  private maxConcurrent = 5;

  async add(task: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push(async () => {
        try {
          await task();
          resolve();
        } catch (error) {
          reject(error);
        } finally {
          this.running--;
          this.processQueue();
        }
      });
      this.processQueue();
    });
  }

  private processQueue() {
    while (this.running < this.maxConcurrent && this.queue.length > 0) {
      const task = this.queue.shift();
      if (task) {
        this.running++;
        task();
      }
    }
  }
}

/**
 * UploadFiles 统一 Store
 * 方案B: Direct Merge - 在一个 create 函数中直接合并两个 store 的逻辑
 */
export const useUploadFilesStore = create<IUploadFilesStore>((set, get) => {
  return {
    tasks: {},

    // ========== 弹窗/抽屉状态初始化 ==========
    uploadModal: {
      isOpen: false,
      mode: "upload",
      isConfirming: false,
      currentData: null,
      resultBatchId: null, // 见 setUploadModalResultBatchId；打开/关闭弹窗时与 currentData 一并清空
      resultDetailSnapshot: null, // 见 setUploadModalResultDetailSnapshot；与 resultBatchId 一并维护
    },
    uploadDrawer: {
      isOpen: false,
      mode: "upload",
      isConfirming: false,
      currentData: null,
    },
    uploadSimpleModal: {
      isOpen: false,
      isConfirming: false,
      currentData: null,
    },

    // ========== UploadModal 控制器方法 ==========
    openUploadModal: (data: IInboundFields) => {
      set((state) => ({
        uploadModal: {
          isOpen: true,
          mode: "upload",
          isConfirming: false,
          currentData: data,
          resultBatchId: null, // 新开会话，不沿用上次失败 batchId
          resultDetailSnapshot: null, // 新开会话，不沿用上次 detail 快照
        },
      }));
    },

    closeUploadModal: () => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          isOpen: false,
          currentData: null,
          resultBatchId: null, // 关闭后清空，避免下次打开误拉旧 detail
          resultDetailSnapshot: null, // 关闭后清空，与 resultBatchId 一致
        },
      }));
    },

    setUploadModalMode: (mode: UploadModalMode) => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          mode,
        },
      }));
    },

    setUploadModalConfirming: (confirming: boolean) => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          isConfirming: confirming,
        },
      }));
    },

    // Inbound Confirm 上传失败分支写入，与 task 解耦，仅用于结果页 failedList 请求
    setUploadModalResultBatchId: (batchId: string | null) => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          resultBatchId: batchId,
        },
      }));
    },

    setUploadModalResultDetailSnapshot: (data: IDetailResponse["data"] | null) => {
      set((state) => ({
        uploadModal: {
          ...state.uploadModal,
          resultDetailSnapshot: data,
        },
      }));
    },

    resetUploadModal: () => {
      set({
        uploadModal: {
          isOpen: false,
          mode: "upload",
          isConfirming: false,
          currentData: null,
          resultBatchId: null,
          resultDetailSnapshot: null,
        },
      });
    },

    // ========== UploadDrawer 控制器方法 ==========
    openUploadDrawer: (data: any) => {
      set((state) => ({
        uploadDrawer: {
          isOpen: true,
          mode: "upload",
          isConfirming: false,
          currentData: data,
        },
      }));
    },

    closeUploadDrawer: () => {
      set((state) => ({
        uploadDrawer: {
          ...state.uploadDrawer,
          isOpen: false,
          currentData: null,
        },
      }));
    },

    setUploadDrawerMode: (mode: UploadModalMode) => {
      set((state) => ({
        uploadDrawer: {
          ...state.uploadDrawer,
          mode,
        },
      }));
    },

    setUploadDrawerConfirming: (confirming: boolean) => {
      set((state) => ({
        uploadDrawer: {
          ...state.uploadDrawer,
          isConfirming: confirming,
        },
      }));
    },

    resetUploadDrawer: () => {
      set({
        uploadDrawer: {
          isOpen: false,
          mode: "upload",
          isConfirming: false,
          currentData: null,
        },
      });
    },

    // ========== UploadSimpleModal 控制器方法 ==========
    openUploadSimpleModal: (data: any) => {
      set((state) => ({
        uploadSimpleModal: {
          isOpen: true,
          isConfirming: false,
          currentData: data,
        },
      }));
    },

    closeUploadSimpleModal: () => {
      set((state) => ({
        uploadSimpleModal: {
          ...state.uploadSimpleModal,
          isOpen: false,
          currentData: null,
        },
      }));
    },

    setUploadSimpleModalConfirming: (confirming: boolean) => {
      set((state) => ({
        uploadSimpleModal: {
          ...state.uploadSimpleModal,
          isConfirming: confirming,
        },
      }));
    },

    resetUploadSimpleModal: () => {
      set({
        uploadSimpleModal: {
          isOpen: false,
          isConfirming: false,
          currentData: null,
        },
      });
    },

    // ========== 任务管理方法 ==========
    getOrCreateTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => {
      const state = get();
      const existingTask = state.tasks[taskId];
      if (existingTask) {
        return existingTask;
      }

      const newTask: IUploadTask = {
        taskId,
        fileList: [],
        status: "init",
        sellerInfo,
      };

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: newTask,
        },
      }));

      return newTask;
    },

    addFiles: (taskId: string, files: ILoadFile[]) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      const existingKeys = new Set(task.fileList.map((f) => getFileKey(f.file)));
      const newFiles = files.filter((f) => !existingKeys.has(getFileKey(f)));

      // .error 来自 FileUploadZone / UploadFiles 的前端校验；用 excluded 且不落 result，列表文案由组件按改前路径现算
      const newFileItems: IUploadFileItem[] = newFiles.map((file) => ({
        file,
        status: (file as ILoadFile & { error?: string }).error ? "excluded" : "init",
        result: undefined,
      }));

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: [...task.fileList, ...newFileItems],
          },
        },
      }));
    },

    removeFiles: (taskId: string, fileKeys: string[]) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      const keySet = new Set(fileKeys);
      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: task.fileList.filter((item) => !keySet.has(getFileKey(item.file))),
          },
        },
      }));
    },

    updateFileStatus: (
      taskId: string,
      fileKey: string,
      status: IUploadFileItemStatus,
      result?: IUploadFileItem["result"],
    ) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: task.fileList.map((item) =>
              getFileKey(item.file) === fileKey ? { ...item, status, result } : item,
            ),
          },
        },
      }));
    },

    clearTask: (taskId: string) => {
      set((state) => {
        const { [taskId]: removed, ...rest } = state.tasks;
        return { tasks: rest };
      });
    },

    clearBulkUploadTasks: () => {
      set((state) => {
        const rest = Object.fromEntries(
          Object.entries(state.tasks).filter(
            ([taskId]) => !taskId.startsWith("document-center-batch-"),
          ),
        );
        return { tasks: rest };
      });
    },

    resetTask: (taskId: string, sellerInfo?: IUploadTask["sellerInfo"]) => {
      const state = get();
      const task = state.tasks[taskId];

      if (!task) {
        if (!sellerInfo) {
          return;
        }
        get().getOrCreateTask(taskId, sellerInfo);
        return;
      }

      if (task.status === "uploading") {
        return;
      }

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            fileList: [],
            status: "init",
          },
        },
      }));
    },

    updateTaskStatus: (taskId: string, status: UploadTaskStatus) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task) return;

      set((state) => ({
        tasks: {
          ...state.tasks,
          [taskId]: {
            ...task,
            status,
          },
        },
      }));
    },

    getTask: (taskId: string) => {
      return get().tasks[taskId];
    },

    hasUploadingTasks: () => {
      const tasks = get().tasks;
      return Object.values(tasks).some((task) => task.status === "uploading");
    },

    // 按 uploadResultDtoList × matchResult 执行；Drawer/Simple/草稿勿传 exposeBatchId（不写 resultBatchId、无结果弹框）；仅 UploadModal Confirm 传 inboundConfirmAfterS3FetchDetail
    uploadFilesToS3: async (
      taskId: string,
      uploadResponse: IUploadSingleFileResponse,
      onComplete?: (taskStatus: "uploaded" | "error", meta?: { batchId: string }) => void,
      options?: {
        exposeBatchIdForResultModal?: boolean;
        inboundConfirmAfterS3FetchDetail?: boolean;
        onInboundConfirmDetail?: (
          result: InboundConfirmDetailCallbackPayload,
        ) => void | Promise<void>;
      },
    ) => {
      const state = get();
      const task = state.tasks[taskId];
      if (!task || task.fileList.length === 0) return;

      const accountId = getCurrentAccountId() || "";

      get().updateTaskStatus(taskId, "uploading");

      // excluded 不参与本轮 S3；仅 init 进入 loading（与 uploadResultDtoList 子集一致）
      task.fileList.forEach((item) => {
        if (item.status === "init") {
          get().updateFileStatus(taskId, getFileKey(item.file), "loading");
        }
      });

      try {
        if (!uploadResponse.success || !uploadResponse.data.uploadResultDtoList) {
          // 接口整体失败：只给本会参与上传的行打 error；excluded 为纯前端态，不覆盖
          task.fileList.forEach((item) => {
            if (item.status === "excluded") return;
            get().updateFileStatus(taskId, getFileKey(item.file), "error", {
              fileName: item.file.metadata.name,
              uploadUrl: "",
              error: uploadResponse.message || "Failed to get upload URL",
            });
          });
          get().updateTaskStatus(taskId, "error");
          throw new Error(uploadResponse.message || "Failed to get upload URL");
        }

        const batchId = uploadResponse.data.batchId; // 回传给 onComplete，供 Inbound 结果页 detail

        const fileMap = new Map<string, ILoadFile>();
        task.fileList.forEach((item) => {
          fileMap.set(getFileKey(item.file), item.file);
        });

        const uploadQueue = new UploadQueue();
        const uploadPromises = uploadResponse.data.uploadResultDtoList.map((result) => {
          const file = fileMap.get(result.filePath);
          if (!file) {
            // DTO 里有路径但当前 task 无对应文件（异常数据），跳过
            return Promise.resolve();
          }

          // 按 matchResult 逐条：failReason→失败且不 callback；无原因无 URL→跳过；有 URL→S3+callback
          return uploadQueue.add(async () => {
            const matches = result.matchResult || [];
            let anyMatchFailReason = false;
            let anyS3Fail = false;
            let firstUploadUrl = "";

            for (const m of matches) {
              const failReasonText = m.failReason?.trim?.();
              if (failReasonText) {
                anyMatchFailReason = true; // 不调 S3、不调 callbackResult（产品约定）
                continue;
              }

              const url = m.uploadUrl?.trim?.();
              if (!url) {
                // 无原因且无 URL：忽略（非失败非成功）；后端常伴 success true
                continue;
              }

              if (!firstUploadUrl) {
                firstUploadUrl = url;
              }

              let uploadSuccess = false;
              try {
                await uploadToS3(url, file.blob);
                uploadSuccess = true;
              } catch {
                anyS3Fail = true;
              }
              try {
                await callbackResult({
                  accountId,
                  batchId,
                  filePath: result.filePath,
                  fileHash: result.fileHash,
                  sellerId: m.sellerId,
                  shipmentId: m.shipmentId,
                  storagePath: m.storagePath ?? "", // 接口字段必填，避免 undefined
                  uploadResult: uploadSuccess,
                  fileId: result.fileId,
                });
              } catch {
                // callback 失败不阻塞队列；文件成败已由 S3 结果体现
              }
            }

            // 本地 error 仅用于触发「进结果页」等，具体原因以 document-center detail 为准
            if (anyMatchFailReason || anyS3Fail) {
              get().updateFileStatus(taskId, getFileKey(file), "error", {
                fileName: result.fileName,
                uploadUrl: "",
                error: "Upload failed",
              });
            } else {
              get().updateFileStatus(taskId, getFileKey(file), "loaded", {
                fileName: result.fileName,
                uploadUrl: firstUploadUrl, // 仅 URL 路径时有值；全跳过 match 时为空串
              });
            }
          });
        });

        await Promise.allSettled(uploadPromises);

        const updatedTask = get().tasks[taskId];
        if (!updatedTask) {
          return;
        }

        // 仅 uploadResultDtoList 出现的路径上的 error 计入 task；excluded 永不参与（Success Tab 仍依赖本地行状态；是否进 Result/confirm 由 Inbound 分支里 detail.failedList 决定）
        const dtoPathSet = new Set(uploadResponse.data.uploadResultDtoList.map((r) => r.filePath));
        const hasError = updatedTask.fileList.some(
          (item) => item.status === "error" && dtoPathSet.has(getFileKey(item.file)),
        );
        const taskStatus: "uploaded" | "error" = hasError ? "error" : "uploaded";

        get().updateTaskStatus(taskId, taskStatus);

        // Inbound Confirm：与普通上传分流，不在此调用 onComplete；由 Footer 根据 detail.failedList 决定 Result 或直接 confirm
        if (options?.inboundConfirmAfterS3FetchDetail) {
          const detailCb = options.onInboundConfirmDetail;
          try {
            const detailRes = await getUploadHistoryDetail({ accountId, batchId });
            if (!detailRes.success || !detailRes.data) {
              await detailCb?.({
                kind: "detail_error",
                batchId,
                message: detailRes.message,
              });
              return;
            }
            await detailCb?.({ kind: "detail_ok", batchId, data: detailRes.data });
          } catch {
            await detailCb?.({ kind: "detail_error", batchId, message: null });
          }
          return;
        }

        // 非 Confirm：保持原行为（草稿/Drawer/Simple 等）
        onComplete?.(taskStatus, options?.exposeBatchIdForResultModal ? { batchId } : undefined);
      } catch (error) {
        get().updateTaskStatus(taskId, "error");
        throw error;
      }
    },
  };
});
